import React, { useMemo } from "react";
import { formatMoney } from "../../lib/format";

const NODE_W = 168;
const NODE_H = 86;
const ROW_GAP = 48;
const COL_GAP = 28;
const PAD_TOP = 52;
const HEADER_H = 32;

const COLUMN_LABELS = [
  "Checkout",
  "Order split",
  "Line items",
  "Café share",
  "Platform share",
  "PG fees",
  "Settlement",
];

const FLOW_C0 = 88;
const FLOW_C1 = FLOW_C0 + NODE_W + COL_GAP;
const FLOW_C2 = FLOW_C1 + NODE_W + COL_GAP;
const FLOW_C3 = FLOW_C2 + NODE_W + COL_GAP;
const FLOW_C4 = FLOW_C3 + NODE_W + COL_GAP;
const FLOW_C5 = FLOW_C4 + NODE_W + COL_GAP;
const FLOW_C6 = FLOW_C5 + NODE_W + COL_GAP;
const FLOW_WIDTH = FLOW_C6 + NODE_W + 48;
const FLOW_COLUMNS = [FLOW_C0, FLOW_C1, FLOW_C2, FLOW_C3, FLOW_C4, FLOW_C5, FLOW_C6];

const FLOW_MENU_ROW_Y = PAD_TOP + HEADER_H;
const FLOW_ADDONS_ROW_Y = FLOW_MENU_ROW_Y + NODE_H + ROW_GAP;
const FLOW_DELIVERY_ROW_Y = FLOW_ADDONS_ROW_Y + NODE_H + ROW_GAP;
const FLOW_HEIGHT = FLOW_DELIVERY_ROW_Y + NODE_H + 40;

const FLOW_CUSTOMER_Y = FLOW_MENU_ROW_Y + (FLOW_ADDONS_ROW_Y - FLOW_MENU_ROW_Y) / 2 - NODE_H / 2;
const FLOW_ITEMS_Y = FLOW_MENU_ROW_Y;
const FLOW_DELIVERY_Y = FLOW_DELIVERY_ROW_Y;
const FLOW_MENU_Y = FLOW_MENU_ROW_Y;
const FLOW_ADDONS_Y = FLOW_ADDONS_ROW_Y;
const FLOW_PG_CAFE_Y = FLOW_MENU_ROW_Y;
const FLOW_PG_PLATFORM_Y = FLOW_ADDONS_ROW_Y;
const FLOW_PG_DELIVERY_Y = FLOW_DELIVERY_ROW_Y;
const FLOW_CAFE_NET_Y = FLOW_MENU_ROW_Y;
const FLOW_PLATFORM_NET_Y = FLOW_ADDONS_ROW_Y;

function gutterBetween(leftColX, rightColX, nodeW = NODE_W) {
  return (leftColX + nodeW + rightColX) / 2;
}

function pathThroughGutters(x1, y1, x2, y2, gutterXs) {
  if (gutterXs.length === 0) {
    const mid = (x1 + x2) / 2;
    return "M " + x1 + " " + y1 + " H " + mid + " V " + y2 + " H " + x2;
  }
  const [firstGutter, ...restGutters] = gutterXs;
  let d = "M " + x1 + " " + y1 + " H " + firstGutter + " V " + y2;
  restGutters.forEach((gx) => {
    d += " H " + gx;
  });
  d += " H " + x2;
  return d;
}

function guttersFromTo(fromColX, toColX, columns) {
  const fromIdx = columns.indexOf(fromColX);
  const toIdx = columns.indexOf(toColX);
  if (fromIdx < 0 || toIdx < 0 || fromIdx >= toIdx) return [];
  const gutters = [];
  for (let i = fromIdx; i < toIdx; i += 1) {
    gutters.push(gutterBetween(columns[i], columns[i + 1]));
  }
  return gutters;
}

function ColumnHeader({ x, y, w, label }) {
  return (
    <text x={x + w / 2} y={y} textAnchor="middle" className="commission-flow-col-header">
      {label}
    </text>
  );
}

function FlowNode({ x, y, w = NODE_W, h = NODE_H, label, sublabel, amount, tone = "default", rx = 10 }) {
  return (
    <g className={"commission-flow-node commission-flow-node--" + tone}>
      <rect x={x} y={y} width={w} height={h} rx={rx} className="commission-flow-node__box" />
      <text x={x + w / 2} y={y + (sublabel ? 22 : 28)} textAnchor="middle" className="commission-flow-node__label">
        {label}
      </text>
      {sublabel ? (
        <text x={x + w / 2} y={y + 38} textAnchor="middle" className="commission-flow-node__sublabel">
          {sublabel}
        </text>
      ) : null}
      <text x={x + w / 2} y={y + h - 14} textAnchor="middle" className="commission-flow-node__amount">
        {amount}
      </text>
    </g>
  );
}

function FlowLink({ d, stroke, className = "commission-flow-link", strokeDasharray }) {
  return <path d={d} className={className} stroke={stroke} strokeDasharray={strokeDasharray} fill="none" />;
}

function RowBand({ y, h, className, label, fill, textFill }) {
  return (
    <g className={className} aria-hidden="true">
      <rect x={8} y={y} width={FLOW_WIDTH - 16} height={h} rx={8} className="commission-flow-row-band" fill={fill} />
      <text x={18} y={y + 18} className="commission-flow-row-label" fill={textFill}>
        {label}
      </text>
    </g>
  );
}

export function CommissionDistributionFlow({ flow }) {
  const links = useMemo(() => {
    const cy = (topY) => topY + NODE_H / 2;
    const right = (colX) => colX + NODE_W;
    const left = (colX) => colX;
    const g = (fromCol, toCol) => guttersFromTo(fromCol, toCol, FLOW_COLUMNS);

    const link = (fromCol, fromTopY, toCol, toTopY, opts = {}) => {
      const { laneOffset = 0, stroke, strokeDasharray, className } = opts;
      const x1 = right(fromCol);
      const y1 = cy(fromTopY) + laneOffset;
      const x2 = left(toCol);
      const y2 = cy(toTopY) + laneOffset;
      const d = pathThroughGutters(x1, y1, x2, y2, g(fromCol, toCol));
      return { d, stroke, strokeDasharray, className };
    };

    return [
      link(FLOW_C0, FLOW_CUSTOMER_Y, FLOW_C1, FLOW_ITEMS_Y, { className: "commission-flow-link--items" }),
      link(FLOW_C0, FLOW_CUSTOMER_Y, FLOW_C1, FLOW_DELIVERY_Y, { className: "commission-flow-link--delivery" }),
      link(FLOW_C1, FLOW_ITEMS_Y, FLOW_C2, FLOW_MENU_Y, { className: "commission-flow-link--items" }),
      link(FLOW_C1, FLOW_ITEMS_Y, FLOW_C2, FLOW_ADDONS_Y, { className: "commission-flow-link--cafe", laneOffset: 5 }),
      link(FLOW_C2, FLOW_MENU_Y, FLOW_C3, FLOW_MENU_Y, { className: "commission-flow-link--cafe" }),
      link(FLOW_C2, FLOW_MENU_Y, FLOW_C4, FLOW_MENU_Y, { className: "commission-flow-link--commission" }),
      link(FLOW_C2, FLOW_ADDONS_Y, FLOW_C4, FLOW_ADDONS_Y, { className: "commission-flow-link--commission", laneOffset: 5 }),
      
      // Cafe base -> PG cafe bears
      link(FLOW_C3, FLOW_MENU_Y, FLOW_C5, FLOW_PG_CAFE_Y, { className: "commission-flow-link--cafe" }),
      // Platform commission -> PG platform bears
      link(FLOW_C4, FLOW_MENU_Y, FLOW_C5, FLOW_PG_PLATFORM_Y, { stroke: "#c55a1d" }),
      link(FLOW_C4, FLOW_ADDONS_Y, FLOW_C5, FLOW_PG_PLATFORM_Y, { stroke: "#c55a1d", laneOffset: 5 }),
      // Delivery fee -> PG cafe bears delivery
      link(FLOW_C1, FLOW_DELIVERY_Y, FLOW_C5, FLOW_PG_DELIVERY_Y, { className: "commission-flow-link--delivery", stroke: "#1565c0" }),

      // Settlements
      link(FLOW_C3, FLOW_MENU_Y, FLOW_C6, FLOW_CAFE_NET_Y, { className: "commission-flow-link--cafe" }),
      link(FLOW_C2, FLOW_ADDONS_Y, FLOW_C6, FLOW_CAFE_NET_Y, { className: "commission-flow-link--cafe", laneOffset: 8 }),
      link(FLOW_C1, FLOW_DELIVERY_Y, FLOW_C6, FLOW_CAFE_NET_Y, { className: "commission-flow-link--delivery", laneOffset: -8 }),
      
      // Deductions
      {
        d: pathThroughGutters(right(FLOW_C5), cy(FLOW_PG_CAFE_Y), left(FLOW_C6), cy(FLOW_CAFE_NET_Y), g(FLOW_C5, FLOW_C6)),
        className: "commission-flow-link--cafe",
        stroke: "#b71c1c",
        strokeDasharray: "6 4",
      },
      {
        d: pathThroughGutters(right(FLOW_C5), cy(FLOW_PG_DELIVERY_Y), left(FLOW_C6), cy(FLOW_CAFE_NET_Y), g(FLOW_C5, FLOW_C6)),
        className: "commission-flow-link--cafe",
        stroke: "#b71c1c",
        strokeDasharray: "6 4",
      },
      link(FLOW_C4, FLOW_MENU_Y, FLOW_C6, FLOW_PLATFORM_NET_Y, { className: "commission-flow-link--commission", laneOffset: -5 }),
      link(FLOW_C4, FLOW_ADDONS_Y, FLOW_C6, FLOW_PLATFORM_NET_Y, { className: "commission-flow-link--commission", laneOffset: 5 }),
      {
        d: pathThroughGutters(right(FLOW_C5), cy(FLOW_PG_PLATFORM_Y), left(FLOW_C6), cy(FLOW_PLATFORM_NET_Y), g(FLOW_C5, FLOW_C6)),
        stroke: "#c55a1d",
        strokeDasharray: "6 4",
      },
    ];
  }, []);

  const isCod = String(flow.paymentMethod ?? "").toUpperCase() === "COD";
  const effectivePgRate = isCod ? 0 : (flow.pgEffectiveRatePct ?? (flow.pgRatePct === 2 ? 2.36 : (flow.pgRatePct || 2.36)));
  const pgCafeBaseAmount = isCod
    ? 0
    : (flow.pgCafeOnMenuBase ?? flow.pgCafeOnMenu ?? Number(((flow.cafeProductBase ?? 0) * (effectivePgRate / 100)).toFixed(2)));
  const pgPlatformAmount = isCod
    ? 0
    : (flow.pgPlatformBears ?? flow.pgPlatformOnMenu ?? Number(((flow.platformCommission ?? 0) * (effectivePgRate / 100)).toFixed(2)));
  const pgDeliveryAmount = isCod
    ? 0
    : (flow.pgCafeDelivery ?? Number(((flow.deliveryFee ?? 0) * (effectivePgRate / 100)).toFixed(2)));

  const nodes = useMemo(
    () => [
      {
        key: "customer",
        x: FLOW_C0,
        y: FLOW_CUSTOMER_Y,
        w: NODE_W,
        h: NODE_H,
        label: "Customer paid",
        sublabel: "Full charge via PG",
        amount: formatMoney(flow.customerPaid),
        tone: "source",
      },
      {
        key: "items",
        x: FLOW_C1,
        y: FLOW_ITEMS_Y,
        label: "Items subtotal",
        sublabel: formatMoney(flow.menuWithMarkup) + " + " + formatMoney(flow.addons),
        amount: formatMoney(flow.itemsSubtotal),
        tone: "items",
      },
      {
        key: "delivery",
        x: FLOW_C1,
        y: FLOW_DELIVERY_Y,
        label: "Delivery fee",
        sublabel: "Added at checkout",
        amount: formatMoney(flow.deliveryFee),
        tone: "delivery",
      },
      {
        key: "menu",
        x: FLOW_C2,
        y: FLOW_MENU_Y,
        label: "Menu line",
        sublabel: formatMoney(flow.cafeProductBase) + " + " + formatMoney(flow.platformCommissionOnBase) + " platform fee",
        amount: formatMoney(flow.menuWithMarkup),
        tone: "items",
      },
      {
        key: "addons",
        x: FLOW_C2,
        y: FLOW_ADDONS_Y,
        label: "Add-ons",
        sublabel: "Extras (e.g. cheese)",
        amount: formatMoney(flow.addons),
        tone: "cafe",
      },
      {
        key: "cafe-product",
        x: FLOW_C3,
        y: FLOW_MENU_Y,
        label: "Café product",
        sublabel: "Base only",
        amount: formatMoney(flow.cafeProductBase),
        tone: "cafe",
      },
      {
        key: "comm-base",
        x: FLOW_C4,
        y: FLOW_MENU_Y,
        label: "Platform commission",
        sublabel: flow.platformCommissionPct + "% × " + formatMoney(flow.menuWithMarkup) + " menu",
        amount: formatMoney(flow.platformCommissionOnBase),
        tone: "commission",
      },
      {
        key: "comm-addons",
        x: FLOW_C4,
        y: FLOW_ADDONS_Y,
        label: "Platform commission",
        sublabel: flow.platformCommissionPct + "% × " + formatMoney(flow.addons) + " add-ons",
        amount: formatMoney(flow.platformCommissionOnAddons),
        tone: "commission",
      },
      {
        key: "pg-menu",
        x: FLOW_C5,
        y: FLOW_PG_CAFE_Y,
        label: "PG — café bears",
        sublabel: isCod ? "₹0 (COD)" : effectivePgRate + "% on " + formatMoney(flow.cafeProductBase) + " base",
        amount: formatMoney(pgCafeBaseAmount),
        tone: "cafe",
      },
      {
        key: "pg-platform",
        x: FLOW_C5,
        y: FLOW_PG_PLATFORM_Y,
        label: "PG — platform bears",
        sublabel: isCod ? "₹0 (COD)" : effectivePgRate + "% on " + formatMoney(flow.platformCommission) + " comm.",
        amount: formatMoney(pgPlatformAmount),
        tone: "commission",
      },
      {
        key: "pg-delivery",
        x: FLOW_C5,
        y: FLOW_PG_DELIVERY_Y,
        label: "PG — café bears",
        sublabel: isCod ? "₹0 (COD)" : effectivePgRate + "% on delivery only",
        amount: formatMoney(pgDeliveryAmount),
        tone: "delivery",
      },
      {
        key: "cafe-net",
        x: FLOW_C6,
        y: FLOW_CAFE_NET_Y,
        label: "Café net",
        sublabel: "After base & delivery PG",
        amount: formatMoney(flow.cafeNetSettlement),
        tone: "cafe",
      },
      {
        key: "platform-net",
        x: FLOW_C6,
        y: FLOW_PLATFORM_NET_Y,
        label: "Platform net",
        sublabel: formatMoney(flow.platformCommission) + " − platform PG",
        amount: formatMoney(flow.platformNet),
        tone: "commission",
      },
    ],
    [flow, isCod, effectivePgRate, pgCafeBaseAmount, pgPlatformAmount, pgDeliveryAmount]
  );

  const bandMenuH = NODE_H + ROW_GAP / 2;
  const bandAddonsH = NODE_H + ROW_GAP;
  const bandDeliveryH = NODE_H + 24;

  return (
    <div className="commission-flow-wrap commission-distribution-flow">
      <svg viewBox={"0 0 " + FLOW_WIDTH + " " + FLOW_HEIGHT} className="commission-flow-svg w-100" role="img" aria-label="Commission and payment gateway distribution">
        <RowBand
          y={FLOW_MENU_ROW_Y - 12}
          h={bandMenuH}
          className="commission-flow-row-band-wrap--menu"
          label="Menu path"
          fill="rgba(197, 90, 29, 0.06)"
          textFill="#8b3d12"
        />
        <RowBand
          y={FLOW_ADDONS_ROW_Y - ROW_GAP / 2}
          h={bandAddonsH}
          className="commission-flow-row-band-wrap--addons"
          label="Add-ons path"
          fill="rgba(46, 125, 50, 0.06)"
          textFill="#2e7d32"
        />
        <RowBand
          y={FLOW_DELIVERY_ROW_Y - ROW_GAP / 2}
          h={bandDeliveryH}
          className="commission-flow-row-band-wrap--delivery"
          label="Delivery"
          fill="rgba(21, 101, 192, 0.06)"
          textFill="#1565c0"
        />

        {COLUMN_LABELS.map((label, i) => (
          <ColumnHeader key={label} x={FLOW_COLUMNS[i]} y={PAD_TOP + 10} w={NODE_W} label={label} />
        ))}

        <g className="commission-flow-links" aria-hidden="true">
          {links.map((edge, i) => (
            <FlowLink key={"edge-" + i} {...edge} />
          ))}
        </g>
        <g className="commission-flow-nodes">
          {nodes.map((node) => (
            <FlowNode
              key={node.key}
              x={node.x}
              y={node.y}
              w={node.w ?? NODE_W}
              h={node.h ?? NODE_H}
              label={node.label}
              sublabel={node.sublabel}
              amount={node.amount}
              tone={node.tone}
              rx={node.rx}
            />
          ))}
        </g>
      </svg>
      <p className="small text-secondary mb-0 mt-3 commission-flow-hint">
        Read left to right. Items = {formatMoney(flow.menuWithMarkup)} + {formatMoney(flow.addons)} ={" "}
        <strong>{formatMoney(flow.itemsSubtotal)}</strong>. Platform commission is {flow.platformCommissionPct}% flat
        on items subtotal ({formatMoney(flow.platformCommission)}). Customer pays {formatMoney(flow.customerPaid)} total.
      </p>
    </div>
  );
}
